import { type EditorView } from '$/pages/CustomEditorPage/utils/migrations';
import { type EditorComponent } from '$/stores/useCustomEditorStore';

export type ComponentMaterial = Pick<
  EditorComponent,
  'materialId' | 'color' | 'shouldDisplayTexture'
>;

export type ComponentTexture = {
  src: string;
  textureRealX: number;
  textureRealY: number;
} | null;

export const deleteView = (views: EditorView[], viewIndex: number) => {
  const nextViews = views.filter((_, index) => index !== viewIndex);

  if (viewIndex !== 0 || nextViews.length === 0) return nextViews;

  // Groups point at components in the first view. When that view is removed,
  // promote the matching components in the new first view to group bases.
  const replacementIds = new Map<string, string>();
  const [newBasis, ...remainingViews] = nextViews;

  const basisComponents = newBasis.components.map((component) => {
    if (!component.groupId) return component;

    replacementIds.set(component.groupId, component.id);
    return { ...component, groupId: component.id };
  });

  return [
    { ...newBasis, components: basisComponents },
    ...remainingViews.map((view) => ({
      ...view,
      components: view.components.map((component) => ({
        ...component,
        groupId: component.groupId
          ? replacementIds.get(component.groupId)
          : undefined,
      })),
    })),
  ];
};

// Applies a material change to a component of a serialized view: updates
// the component fields and the colors / texture of its elements.
export const applyMaterialToView = (
  view: EditorView,
  component: EditorComponent,
  material: ComponentMaterial,
  texture: ComponentTexture,
): EditorView => {
  const elementIds = new Set(component.elements);

  return {
    ...view,
    components: view.components.map((current) =>
      current.id === component.id ? { ...current, ...material } : current,
    ),
    elements: view.elements.map((element) => {
      if (!elementIds.has(element.id)) return element;

      if (element.type === 'grid') {
        return {
          ...element,
          fillMaterial: texture,
          ...(texture && { showTestStructure: false }),
        };
      }

      if (element.type === 'boolean') {
        return { ...element, fillColor: material.color };
      }

      if ('strokeColor' in element) {
        return { ...element, strokeColor: material.color };
      }

      return element;
    }),
  };
};
